import { useState, useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "react-query";
import { Auth } from "shared/@auth/Auth";

export function useDocumentsQueries(options = { enableExtras: false }) {
  const { makeAuthedRequest } = Auth.useContainer();

  const location = useLocation();
  function pathParam(location) {
    return location.startsWith("/management-team") ? "MT" : "EC";
  }
  // Initialize immediately to avoid an initial null/undefined request
  const [_location, setLocation] = useState(pathParam(location.pathname));

  useEffect(() => {
    setLocation(pathParam(location.pathname));
  }, [location.pathname]);

  function dataURItoBlob(data) {
    var byteString = data;
    try {
      let dataURI = data.split(",")[1];
      byteString = window.atob(dataURI);
    } catch {
      //if it failed on atob => already a binary
    }

    const arrayBuffer = new ArrayBuffer(byteString.length);
    const int8Array = new Uint8Array(arrayBuffer);
    for (let i = 0; i < byteString.length; i++) {
      int8Array[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([int8Array], { type: "application/pdf" });
    return blob;
  }

  const downloadFile = async (downloadPath) => {
    return await makeAuthedRequest(
      `/management-team/downloadfile/${downloadPath}`
    )
      .then(async (res) => {
        if (res.ok) {
          let data = await res.arrayBuffer();
          return data;
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      })
      .then((data) => {
        const blob = new Blob([data], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);

        // to open the PDF in a new window
        window.open(url, "_blank");
      });
  };

  const {
    data: unassignedDocuments,
    status: unassignedStatus,
    refetch: unassignedRefetch,
  } = useQuery(
    ["documents/unassigned", _location],
    () =>
      makeAuthedRequest(
        `/management-team/documents/${_location}/unassigned`
      ).then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    { enabled: !!options.enableExtras && !!_location }
  );

  const {
    data: reviewDocuments,
    status: reviewStatus,
    refetch: reviewRefetch,
  } = useQuery(
    ["documents", _location],
    () =>
      makeAuthedRequest(`/management-team/documents/${_location}/review`).then(
        async (res) => {
          if (res.ok) {
            return res.json();
          } else {
            const body = await res.json();
            throw new Error(body.message);
          }
        }
      ),
    { enabled: !!options.enableExtras && !!_location }
  );

  const {
    data: refDocuments,
    status: refStatus,
    refetch: refRefetch,
  } = useQuery(
    ["reference", _location],
    () =>
      makeAuthedRequest(
        `/management-team/documents/${_location}/reference`
      ).then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      }),
    { enabled: !!options.enableExtras && !!_location }
  );

  return {
    reviewDocuments,
    reviewRefetch,
    refDocuments,
    refRefetch,
    reviewStatus,
    refStatus,
    unassignedDocuments,
    unassignedStatus,
    unassignedRefetch,
    downloadFile,
  };
}

export function useDocumentsMutations() {
  const { makeAuthedRequest } = Auth.useContainer();
  const queryClient = useQueryClient();

  // Note: mutations will NOT invalidate queries automatically; callers should use onSuccess callbacks to refetch specific data
  const addDocumentMutation = useMutation((info) =>
    makeAuthedRequest(`/management-team/document/add`, {
      method: "POST",
      body: JSON.stringify(info),
    }).then(async (res) => {
      if (!res.ok) {
        const json = await res.json();
        if (json.errors && json.errors.length > 0) {
          throw new Error(json.errors[0].message);
        }
      }
    })
  );

  const editDocumentMutation = useMutation((info) =>
    makeAuthedRequest(`/management-team/document/update`, {
      method: "PUT",
      body: JSON.stringify(info),
    }).then(async (res) => {
      if (!res.ok) {
        const json = await res.json();
        if (json.errors && json.errors.length > 0) {
          throw new Error(json.errors[0].message);
        }
      }
    })
  );

  const docDelete = useMutation(["douments"], async (id) =>
    makeAuthedRequest(`/management-team/document/delete`, {
      method: "DELETE",
      body: JSON.stringify({ id: id }),
    }).then(async (res) => {
      if (res.ok) {
        return res.json();
      } else {
        const body = await res.json();
        throw new Error(body.message);
      }
    })
  );

  const docPublish = useMutation(["douments"], async ({ id, isDraft }) =>
    makeAuthedRequest(`/management-team/document/publish`, {
      method: "PUT",
      body: JSON.stringify({ id: id, isDraft: isDraft }),
    }).then(async (res) => {
      if (res.ok) {
        return res.json();
      } else {
        const body = await res.json();
        throw new Error(body.message);
      }
    })
  );

  // Document Order Put
  const documentOrderPut = useMutation(["documentItemOrder"], (document) =>
    makeAuthedRequest(`/management-team/document/order`, {
      method: "PUT",
      body: JSON.stringify(document),
    }).then(async (res) => {
      if (res.ok) {
        return;
      } else {
        const body = await res.json();
        throw new Error(body.message);
      }
    })
  );

  return {
    addDocumentMutation,
    editDocumentMutation,
    docDelete,
    docPublish,
    documentOrderPut,
  };
}

export function useDocuments(options = { enableExtras: false }) {
  const q = useDocumentsQueries(options);
  const m = useDocumentsMutations();
  return {
    ...q,
    ...m,
  };
}
